import {
  Configuration,
  BrowserCacheLocation,
  LogLevel,
  InteractionType,
  RedirectRequest,
  PublicClientApplication
} from '@azure/msal-browser';
import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { environment } from '../../environments/environment';
import * as CryptoJS from 'crypto-js';

/**
 * Extracts domain hint from an email address.
 * If username contains @, returns the domain part (e.g., user@contoso.com -> contoso.com).
 * If username has no @, returns empty string (no domain hint).
 */
export function extractDomainHint(username: string): string {
  if (!username || !username.includes('@')) return '';
  return username.split('@')[1].trim().toLowerCase();
}

function readCookieValue(cookieName: string): string | undefined {
  const prefix = `${cookieName}=`;
  const entry = document.cookie
    .split('; ')
    .find(item => item.startsWith(prefix));
  return entry ? entry.substring(prefix.length) : undefined;
}

function decryptCookieValue(encryptedValue: string): string | undefined {
  try {
    const key = CryptoJS.enc.Utf8.parse(environment.cryptingKey);
    let value: string | undefined;
    try {
      const decodedValue = decodeURIComponent(encryptedValue);
      const decrypted = CryptoJS.AES.decrypt(decodedValue, key, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 });
      value = decrypted.toString(CryptoJS.enc.Utf8);
    } catch {}
    if (!value || !value.trim()) {
      const decrypted = CryptoJS.AES.decrypt(encryptedValue, key, { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 });
      value = decrypted.toString(CryptoJS.enc.Utf8);
    }
    return value && value.trim() ? value.trim() : undefined;
  } catch {
    return undefined;
  }
}

export function getDomainHintFromCookie(): string | undefined {
  const encrypted = readCookieValue('authTypeDomain');
  if (!encrypted) return undefined;
  return decryptCookieValue(encrypted);
}

export function getLoginHintFromCookie(): string | undefined {
  const encrypted = readCookieValue('authUser');
  if (!encrypted) return undefined;
  return decryptCookieValue(encrypted);
}

export const msalConfig: Configuration = {
  auth: {
    clientId: environment.msalClientId,
    authority: `${environment.msalB2CInstance}${environment.msalB2CDomain}/${environment.msalB2CPolicy}`,
    knownAuthorities: [environment.msalB2CInstance.replace('https://', '').replace('/', '')],
    redirectUri: environment.msalRedirectUri,
    postLogoutRedirectUri: environment.msalPostLogoutRedirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
  },
  system: {
    loggerOptions: {
      loggerCallback: (_level: LogLevel, _message: string, containsPii: boolean) => {
        if (containsPii) return;
      },
      logLevel: LogLevel.Error,
      piiLoggingEnabled: false,
    },
  },
};

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: () => {
      const domainHint = getDomainHintFromCookie();
      const loginHint = getLoginHintFromCookie();
      const request: RedirectRequest = {
        scopes: ['openid', 'profile', 'offline_access', environment.msalApiScope],
        redirectUri: environment.msalRedirectUri,
      };
      if (loginHint) {
        request.loginHint = loginHint;
      }
      if (domainHint) {
        (request as any).extraQueryParameters = { domain_hint: domainHint };
      }
      return request;
    },
    loginFailedRoute: '/login',
  };
}

export function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  // Build the protected resource map with ALL possible backend URL variants.
  // IMPORTANTE: includiamo openid+profile+offline_access oltre al resource scope.
  // Senza questi scope, B2C ha un bug noto per cui acquireTokenSilent ritorna
  // un AuthenticationResult con accessToken="" (cfr. workaround in main.ts).
  const protectedResourceMap = new Map<string, Array<string>>();
  const scopes = ['openid', 'profile', 'offline_access', environment.msalApiScope];

  const urls = new Set([
    environment.baseUrlADApi,
    environment.devUrlADApi,
    environment.demoUrlApi,
    environment.baseUrlADB2CApi,
  ]);
  urls.forEach(url => {
    if (url) protectedResourceMap.set(url, scopes);
  });

  // AuthN Service (user profile, change password)
  if (environment.baseUrlAuthN) {
    protectedResourceMap.set(environment.baseUrlAuthN, scopes);
  }

  return {
    // Popup: se acquireTokenSilent fallisce, tenta popup (fallisce silenziosamente se bloccato).
    // NON usare Redirect — causa redirect automatico a B2C da chiamate come SignalR.
    interactionType: InteractionType.Popup,
    protectedResourceMap,
    authRequest: () => {
      const domainHint = getDomainHintFromCookie();
      const loginHint = getLoginHintFromCookie();
      const request: RedirectRequest = {
        scopes: ['openid', 'profile', 'offline_access', environment.msalApiScope],
        redirectUri: environment.msalRedirectUri,
      };
      if (loginHint) {
        request.loginHint = loginHint;
      }
      if (domainHint) {
        (request as any).extraQueryParameters = { domain_hint: domainHint };
      }
      return request;
    },
  };
}

export const msalInstance = new PublicClientApplication(msalConfig);
